/**
 * Llamadas públicas al API (sin auth) para propuestas, listados del tenant y detalle de vehículo.
 *
 * **Listado tenant:** `GET {{baseUrl}}/tenant/public/subdomain/:subdomain/vehicles`
 *
/**
 * **Propuesta (landing):** `GET {{baseUrl}}/public/p/:uuid`
 *   — `baseUrl` = `import.meta.env.VITE_API_URL` (sin barra final).
 *   — Respuesta esperada: cuerpo tipo `{ status?: 200, data: { uuid, name, vehicles: [...] } }` o equivalente.
 *
 * **Vehículo embebido:** en `data.vehicles[]` el backend puede enviar `vehicle: { ... }` con el
 *   detalle completo; en ese caso no hace falta `GET /vehicle/:uuid` por fila.
 *
 * **Vehículo suelto (fallback):** `GET {{baseUrl}}/vehicle/:uuid` si la línea solo trae `vehicleUuid`.
 *
 * **Feedback / like (público):** `POST {{baseUrl}}/public/proposal/:uuid/vehicle/:vehicleUuid/feedback`
 *   cuerpo `{ customerLiked: boolean, customerComment: string | null }`.
 */

import { resolveMediaUrl, getApiBaseUrl } from './tenantConfig'

/** @param {{ status?: string, raw?: { status?: string } } | null | undefined} vehicle */
export function isVehicleSold(vehicle) {
  const status = String(vehicle?.status ?? vehicle?.raw?.status ?? '')
    .trim()
    .toUpperCase()
  return status === 'SOLD'
}

function sortVehicleImages(images) {
  if (!Array.isArray(images)) return []
  return [...images].sort((a, b) => {
    const coverA = a?.isCover ? 0 : 1
    const coverB = b?.isCover ? 0 : 1
    if (coverA !== coverB) return coverA - coverB
    return (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0)
  })
}

function resolveVehicleImage(apiBase, item) {
  if (item == null) return null
  if (typeof item === 'string') return resolveMediaUrl(apiBase, item)
  const path =
    item.url ?? item.path ?? item.imageUrl ?? item.src ?? item.objectKey ?? item.fileName
  return resolveMediaUrl(apiBase, path)
}

function formatTransmission(code) {
  if (code == null || String(code).trim() === '') return ''
  const c = String(code).toUpperCase()
  if (c === 'AUTOMATIC' || c === 'AUTOMÁTICA' || c === 'AUTOMATICA') return 'automática'
  if (c === 'MANUAL') return 'manual'
  return String(code).trim()
}

function formatFuel(code) {
  if (code == null || String(code).trim() === '') return ''
  const c = String(code).toUpperCase()
  if (c === 'GASOLINE' || c === 'NAFTA') return 'nafta'
  if (c === 'DIESEL' || c === 'GASOIL') return 'gasoil'
  if (c === 'ELECTRIC' || c === 'ELÉCTRICO' || c === 'ELECTRICO') return 'eléctrico'
  if (c === 'HYBRID' || c === 'HÍBRIDO' || c === 'HIBRIDO') return 'híbrido'
  return String(code).trim()
}

function readCatalogName(field) {
  if (field == null) return ''
  if (typeof field === 'string') return field.trim()
  if (typeof field === 'object' && field.name != null) return String(field.name).trim()
  return ''
}

/** Unifica `vehicleCatalogConfiguration` y `catalog` (plano o anidado). */
export function normalizeVehicleCatalog(v) {
  const cfg = v?.vehicleCatalogConfiguration || {}
  const cat = v?.catalog || {}

  const brandName =
    readCatalogName(cfg.brandName) ||
    readCatalogName(cat.brandName) ||
    readCatalogName(cat.brand) ||
    ''
  const modelName =
    readCatalogName(cfg.modelName) ||
    readCatalogName(cat.modelName) ||
    readCatalogName(cat.model) ||
    ''
  const versionName =
    readCatalogName(cfg.versionName) ||
    readCatalogName(cat.versionName) ||
    readCatalogName(cat.version) ||
    ''
  const year = cfg.year ?? cat.year ?? null
  const fuelTypeCode =
    cfg.fuelTypeCode ?? cat.fuelTypeCode ?? readCatalogName(cat.fuel) ?? null
  const transmissionCode =
    cfg.transmissionCode ?? cat.transmissionCode ?? readCatalogName(cat.transmission) ?? null
  const bodyTypeCode =
    cfg.bodyTypeCode ?? cat.bodyTypeCode ?? readCatalogName(cat.category) ?? null
  const doorsCount = cfg.doorsCount ?? cat.doorsCount ?? null

  return {
    year,
    brandName,
    modelName,
    versionName,
    fuelTypeCode,
    transmissionCode,
    bodyTypeCode,
    doorsCount,
  }
}

function getApiBase() {
  return getApiBaseUrl()
}

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true'

function extractData(body) {
  if (!body || typeof body !== 'object') return null
  return body.data != null ? body.data : null
}

async function apiGetJson(path) {
  const base = getApiBase()
  if (!base) {
    throw new Error('Falta VITE_API_URL')
  }
  const res = await fetch(`${base}${path.startsWith('/') ? path : `/${path}`}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = typeof body.message === 'string' && body.message ? body.message : `Error ${res.status}`
    throw new Error(msg)
  }
  if (body.status != null && body.status !== 200 && body.status !== 201) {
    const msg = typeof body.message === 'string' && body.message ? body.message : `Error ${body.status}`
    throw new Error(msg)
  }
  return body
}

/**
 * @param {string} path
 * @param {object} jsonBody
 */
async function apiPostJson(path, jsonBody) {
  const base = getApiBase()
  if (!base) {
    throw new Error('Falta VITE_API_URL')
  }
  const res = await fetch(`${base}${path.startsWith('/') ? path : `/${path}`}`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(jsonBody),
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = typeof body.message === 'string' && body.message ? body.message : `Error ${res.status}`
    throw new Error(msg)
  }
  if (body.status != null && body.status !== 200 && body.status !== 201) {
    const msg = typeof body.message === 'string' && body.message ? body.message : `Error ${body.status}`
    throw new Error(msg)
  }
  return body
}

/** Normaliza un vehículo del API a la forma usada en cards / detalle. */
export function mapVehicleApiToUi(v, apiBaseUrl) {
  if (!v || typeof v !== 'object') return null
  if (isVehicleSold(v)) return null
  const cfg = normalizeVehicleCatalog(v)
  const titleParts = [cfg.year, cfg.brandName, cfg.modelName, cfg.versionName].filter(
    (x) => x != null && String(x).length > 0,
  )
  const title = titleParts.length > 0 ? titleParts.map(String).join(' ') : 'Vehículo'
  const price = v.priceSale != null && Number.isFinite(Number(v.priceSale)) ? Number(v.priceSale) : 0
  const curRaw = typeof v.currency === 'string' ? v.currency : v.currency?.name
  const cur = String(curRaw || 'USD').trim()
  const priceFormatted = new Intl.NumberFormat('es-UY', { maximumFractionDigits: 0 }).format(price)
  const priceLabel = `${cur} ${priceFormatted}`

  const titleLine1 = [cfg.brandName, cfg.modelName].filter(Boolean).join(' ').trim()
  const titleLine2 = (cfg.versionName && String(cfg.versionName).trim()) || ''

  const imageUrls = sortVehicleImages(v.images)
    .map((img) => resolveVehicleImage(apiBaseUrl, img))
    .filter(Boolean)
  const image = imageUrls[0] ?? null
  const images = imageUrls

  const branchAddress = v.branch?.address != null ? String(v.branch.address).trim() : ''
  const location =
    branchAddress ||
    v.plateLocality?.name ||
    ''

  const kmRaw = v.km
  const mileage =
    kmRaw != null && kmRaw !== '' && Number.isFinite(Number(kmRaw)) ? Number(kmRaw) : null

  return {
    id: v.uuid,
    uuid: v.uuid,
    code: v.uuid ? String(v.uuid).slice(0, 8).toUpperCase() : '—',
    image,
    images,
    title,
    titleLine1: titleLine1 || title,
    titleLine2,
    location,
    price,
    priceTo: null,
    priceLabel,
    doors: cfg.doorsCount != null ? cfg.doorsCount : 4,
    transmission: formatTransmission(cfg.transmissionCode),
    fuel: formatFuel(cfg.fuelTypeCode),
    mileage,
    brand: cfg.brandName || '',
    model: cfg.modelName || '',
    version: cfg.versionName || '',
    year: cfg.year ?? '',
    type: (cfg.bodyTypeCode && String(cfg.bodyTypeCode).toLowerCase()) || 'sedán',
    condition: mileage != null && mileage < 100 ? 'nuevo' : 'usado',
    description: v.description && String(v.description).trim() ? String(v.description).trim() : null,
    status: v.status != null ? String(v.status) : '',
    raw: v,
  }
}

/**
 * Listado público de vehículos del tenant por subdominio.
 * @param {string} subdomain
 * @returns {Promise<Array>}
 */
export async function fetchTenantVehicles(subdomain) {
  const base = getApiBase()
  const s = String(subdomain || '').trim()
  if (!base || !s) return []

  const body = await apiGetJson(`/tenant/public/subdomain/${encodeURIComponent(s)}/vehicles`)
  const list = extractData(body)
  if (!Array.isArray(list)) return []

  return list.map((raw) => mapVehicleApiToUi(raw, base)).filter(Boolean)
}

function normalizeTestimonial(raw, index) {
  if (!raw || typeof raw !== 'object') return null
  const id = String(raw.uuid ?? raw.id ?? `idx-${index}`).trim()
  const name = String(raw.name ?? '').trim()
  const comment = String(raw.comment ?? raw.message ?? '').trim()
  if (!id || !name || !comment) return null
  return { id, name, comment }
}

/**
 * Testimonios públicos del tenant.
 * GET /tenant/public/subdomain/:subdomain/testimonials
 * @param {string} subdomain
 * @returns {Promise<Array<{ id: string, name: string, comment: string }>>}
 */
export async function fetchTenantTestimonials(subdomain) {
  const base = getApiBase()
  const s = String(subdomain || '').trim()
  if (!base || !s) return []

  try {
    const body = await apiGetJson(`/tenant/public/subdomain/${encodeURIComponent(s)}/testimonials`)
    const list = extractData(body)
    if (!Array.isArray(list)) return []
    return list.map(normalizeTestimonial).filter(Boolean)
  } catch {
    return []
  }
}

const MOCK_VEHLIST = [
  {
    uuid: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
    priceSale: 500000,
    currency: { name: 'US$' },
    km: 45000,
    licensePlate: 'SBC 1234',
    plateLocality: { name: 'Las Grutas, Punta del Este' },
    vehicleCatalogConfiguration: {
      year: 2019,
      brandName: 'Toyota',
      modelName: 'RAV4',
      versionName: '4x4',
      doorsCount: 5,
      transmissionCode: 'Automática',
      fuelTypeCode: 'Nafta',
      bodyTypeCode: 'SUV',
    },
  },
  {
    uuid: 'b2c3d4e5-f6a7-8901-bcde-f12345678901',
    priceSale: 28900,
    currency: { name: 'US$' },
    km: 82000,
    licensePlate: 'DAA 9001',
    plateLocality: { name: 'Montevideo' },
    vehicleCatalogConfiguration: {
      year: 2021,
      brandName: 'Peugeot',
      modelName: '208',
      versionName: 'Feline',
      doorsCount: 5,
      transmissionCode: 'Manual',
      fuelTypeCode: 'Nafta',
      bodyTypeCode: 'hatch',
    },
  },
]

const MOCK_VEHICLES = MOCK_VEHLIST.map((raw) => {
  const ui = mapVehicleApiToUi(raw)
  return {
    ...ui,
    id: ui.uuid,
    code: `VH-${String(ui.uuid).slice(0, 6).toUpperCase()}`,
  }
})

const MOCK_PROPOSAL = {
  uuid: 'demo-ana-martinez-LYtH9vCwoU',
  tenantUuid: 'tenant-demo',
  name: 'Propuesta para [Demo] Ana Martínez',
  slug: 'demo-ana-martinez-LYtH9vCwoU',
  status: 'SENT',
  vehicles: [
    {
      vehicleUuid: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
      sortOrder: 0,
      isHighlighted: true,
      customerLiked: null,
      customerComment: null,
    },
    {
      vehicleUuid: 'b2c3d4e5-f6a7-8901-bcde-f12345678901',
      sortOrder: 1,
      isHighlighted: false,
      customerLiked: null,
      customerComment: null,
    },
  ],
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms))
}

/**
 * Objeto `vehicle` anidado en `GET /proposal/:uuid` → mismo shape que `fetchVehiclePublic`.
 * @param {object} vehicleRaw
 * @returns {object | null}
 */
export function mapEmbeddedVehicleToCard(vehicleRaw, apiBaseUrl) {
  if (!vehicleRaw || typeof vehicleRaw !== 'object' || !vehicleRaw.uuid) return null
  const ui = mapVehicleApiToUi(vehicleRaw, apiBaseUrl)
  if (!ui) return null
  return {
    ...ui,
    id: ui.uuid,
    code: `VH-${String(ui.uuid).slice(0, 6).toUpperCase()}`,
  }
}

/**
 * Vehículo embebido en una línea de `GET /public/p/:uuid` (formato con `catalog`).
 * @param {object} line
 * @param {string} [apiBaseUrl]
 */
export function mapProposalVehicleLineToUi(line, apiBaseUrl) {
  if (!line || typeof line !== 'object') return null

  if (line.vehicle) {
    return mapEmbeddedVehicleToCard(line.vehicle, apiBaseUrl)
  }

  const vehicleUuid = String(line.vehicleUuid || line.uuid || '').trim()
  if (!vehicleUuid) return null

  const synthetic = {
    uuid: vehicleUuid,
    priceSale: line.priceSale,
    currency: line.currency,
    km: line.km,
    description: line.description,
    images: line.images,
    catalog: line.catalog,
    vehicleCatalogConfiguration: line.vehicleCatalogConfiguration,
  }

  const ui = mapVehicleApiToUi(synthetic, apiBaseUrl)
  if (!ui) return null
  return {
    ...ui,
    id: ui.uuid,
    code: ui.code || `VH-${String(ui.uuid).slice(0, 6).toUpperCase()}`,
  }
}

async function fetchTenantVehiclesRaw(subdomain) {
  const s = String(subdomain || '').trim()
  if (!s || !getApiBase()) return []
  const body = await apiGetJson(`/tenant/public/subdomain/${encodeURIComponent(s)}/vehicles`)
  const list = extractData(body)
  return Array.isArray(list) ? list : []
}

/**
 * Obtiene la propuesta para la landing.
 * Endpoint: `GET /public/p/{uuid}`
 * @param {string} uuid
 */
export async function fetchProposalPublic(uuid) {
  const id = String(uuid || '').trim()
  if (!id) throw new Error('Falta el identificador de la propuesta.')

  if (USE_MOCK || !getApiBase()) {
    await sleep(350)
    if (
      id === MOCK_PROPOSAL.uuid ||
      id === 'demo' ||
      id === MOCK_PROPOSAL.slug ||
      id.toLowerCase().includes('demo-ana-martinez')
    ) {
      return { ...MOCK_PROPOSAL, uuid: MOCK_PROPOSAL.uuid }
    }
    throw new Error('Propuesta no encontrada.')
  }

  const body = await apiGetJson(`/public/p/${encodeURIComponent(id)}`)
  const data = extractData(body)
  if (data && typeof data === 'object' && data.uuid) return data
  if (body && typeof body === 'object' && body.uuid && !body.data) return body
  throw new Error('Propuesta no encontrada')
}

export async function fetchVehiclePublic(uuid) {
  const id = String(uuid || '').trim()
  if (!id) throw new Error('Falta el identificador del vehículo.')

  if (USE_MOCK || !getApiBase()) {
    await sleep(200)
    const found = MOCK_VEHICLES.find((m) => m.uuid === id)
    if (found) {
      return { ...found, id: found.uuid, code: `VH-${found.uuid.slice(0, 6).toUpperCase()}` }
    }
    throw new Error('Vehículo no encontrado')
  }

  const body = await apiGetJson(`/vehicle/${encodeURIComponent(id)}`)
  const data = extractData(body)
  if (!data || typeof data !== 'object') {
    throw new Error('Vehículo no encontrado')
  }
  const ui = mapVehicleApiToUi(data)
  if (!ui) throw new Error('Vehículo no encontrado')
  return {
    ...ui,
    id: ui.uuid,
    code: `VH-${String(ui.uuid).slice(0, 6).toUpperCase()}`,
  }
}

/**
 * Marca o quita "me gusta" para un vehículo dentro de la propuesta.
 * `POST {{baseUrl}}/public/proposal/:uuid/vehicle/:vehicleUuid/feedback`
 * Cuerpo: `{ customerLiked: boolean }`
 *
 * @param {string} proposalUuid
 * @param {string} vehicleUuid
 * @param {{ customerLiked: boolean }} options
 * @returns {Promise<object>}
 */
export async function postProposalVehicleLike(proposalUuid, vehicleUuid, options) {
  const pid = String(proposalUuid || '').trim()
  const vid = String(vehicleUuid || '').trim()
  if (!pid || !vid) {
    throw new Error('Faltan identificadores de la propuesta o del vehículo.')
  }
  const customerLiked = Boolean(options && options.customerLiked)

  if (USE_MOCK || !getApiBase()) {
    await sleep(200)
    return { customerLiked }
  }

  return apiPostJson(
    `/public/proposal/${encodeURIComponent(pid)}/vehicle/${encodeURIComponent(vid)}/feedback`,
    { customerLiked },
  )
}

/**
 * Carga filas de vehículos para la landing a partir de `GET /public/p/:uuid`.
 * @param {object} proposal - `data` de la propuesta
 * @returns {Promise<Array<{ vehicle: object, line: object }>>}
 */
export async function loadVehiclesForProposal(proposal) {
  const apiBase = getApiBase()
  const list = [...(proposal.vehicles || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))

  const subdomain = String(proposal.tenantSubdomain || '').trim()
  let tenantByUuid = new Map()
  if (subdomain && apiBase && !USE_MOCK) {
    try {
      const rawList = await fetchTenantVehiclesRaw(subdomain)
      for (const raw of rawList) {
        if (raw?.uuid) tenantByUuid.set(String(raw.uuid), raw)
      }
    } catch {
      /* enriquecimiento opcional */
    }
  }

  const rows = []
  for (const line of list) {
    const vid = String(line.vehicleUuid || line.uuid || '').trim()
    const tenantRaw = vid ? tenantByUuid.get(vid) : null

    let vehicle = null
    if (tenantRaw) {
      const merged = {
        ...tenantRaw,
        uuid: vid || tenantRaw.uuid,
        priceSale: line.priceSale ?? tenantRaw.priceSale,
        currency: line.currency ?? tenantRaw.currency,
        km: line.km ?? tenantRaw.km,
        description: line.description ?? tenantRaw.description,
        images: Array.isArray(line.images) && line.images.length > 0 ? line.images : tenantRaw.images,
        catalog: {
          ...(tenantRaw.catalog || {}),
          ...(line.catalog || {}),
        },
      }
      vehicle = mapVehicleApiToUi(merged, apiBase)
      if (vehicle) {
        vehicle = { ...vehicle, id: vehicle.uuid }
      }
    }

    if (!vehicle) {
      vehicle = mapProposalVehicleLineToUi(line, apiBase)
    }

    if (vehicle) {
      rows.push({ vehicle, line })
      continue
    }

    if (!vid) continue
    try {
      const v = await fetchVehiclePublic(vid)
      rows.push({ vehicle: v, line })
    } catch {
      /* se omite la fila */
    }
  }
  return rows
}

